import React, { useEffect, useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import PeopleToolbar from "./PeopleToolbar";
import {
  CUSTOMER_TABS,
  TEAM_TABS,
  GROUP_TABS,
  AGENT_TABS,
} from "../../../constants/peopleData";
import {
  getPeoplePageKey,
  mergePeopleSearchParams,
  filterTeams,
  filterGroups,
} from "../../../utils/peopleHelpers";
import { computeTeamTabCounts } from "../../../utils/teamMappers";
import { computeCustomerTabCounts } from "../../../utils/customerMappers";
import { computeAgentTabCounts } from "../../../utils/agentMappers";
import { fetchAgentsList } from "../../../store/features/agentsApi";
import { fetchAccessibleGroups } from "../../../store/features/groupsApi";
import { refreshCustomerTabCounts } from "../../../store/features/customersApi";
import { useAnalyticsGroupId } from "../../../hooks/useAnalyticsGroupId";

/**
 * Renders below the main dashboard row on /people/* routes.
 * Page title + primary CTA live in DashboardHeader; here: tabs + search/filters (URL search params).
 */
export default function PeopleHeaderExtension() {
  const location = useLocation();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageKey = getPeoplePageKey(location.pathname);
  const { selectedGroupId } = useAnalyticsGroupId();
  const teamsState = useSelector((state) => state.peopleTeams);
  const groupsList = useSelector((state) => state.peopleGroups?.list || []);
  const customerTabTotals = useSelector((state) => state.peopleCustomers?.tabTotals || {});
  const agentTabTotals = useSelector((state) => state.peopleAgents?.tabTotals || {});

  const patch = (updates) => mergePeopleSearchParams(setSearchParams, updates);

  const tab = searchParams.get("tab") || "all";
  const q = searchParams.get("q") || "";

  const customerCounts = useMemo(
    () => computeCustomerTabCounts(customerTabTotals),
    [customerTabTotals]
  );
  // Teams and Groups filter client-side, so counts mirror the same search the list uses.
  const teamCounts = useMemo(() => {
    const listKey = selectedGroupId ? String(selectedGroupId) : "";
    const scopedList =
      (listKey && teamsState?.cacheByListKey?.[listKey]) ||
      (listKey && listKey === teamsState?.lastFetchedListKey
        ? teamsState?.list
        : null) ||
      [];
    return computeTeamTabCounts(filterTeams(scopedList, { search: q }));
  }, [selectedGroupId, teamsState?.cacheByListKey, teamsState?.lastFetchedListKey, teamsState?.list, q]);
  const groupCounts = useMemo(() => {
    const matched = filterGroups(groupsList, { search: q });
    const all = matched.length;
    const active = matched.filter((g) => g.status === "active").length;
    const archived = matched.filter((g) => g.status === "archived").length;
    return { all, active, archived };
  }, [groupsList, q]);

  const agentCounts = useMemo(() => computeAgentTabCounts(agentTabTotals), [agentTabTotals]);

  // Agent counts come from the API, so the search has to go with them.
  // `q` is already debounced by the Agents page before it reaches the URL.
  useEffect(() => {
    if (pageKey !== "agents" || !selectedGroupId) return;
    ["all", "online", "offline", "suspended"].forEach((status) => {
      dispatch(
        fetchAgentsList({
          status,
          group_id: selectedGroupId,
          search: q || undefined,
          countsOnly: true,
        })
      );
    });
  }, [pageKey, dispatch, selectedGroupId, q]);

  useEffect(() => {
    if (pageKey !== "groups") return;
    dispatch(fetchAccessibleGroups());
  }, [pageKey, dispatch]);

  // Initial load only — the Customers page re-runs this whenever the query changes.
  // `q` is read but intentionally not a dependency, to avoid duplicating that refetch.
  useEffect(() => {
    if (pageKey !== "customers" || !selectedGroupId) return;
    dispatch(refreshCustomerTabCounts(selectedGroupId, q));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageKey, dispatch, selectedGroupId]);

  if (!pageKey) return null;

  const setTab = (key) => {
    patch({ tab: key === "all" ? "" : key });
  };

  const customerTabCount = (key) =>
    key === "all" ? customerCounts.all || 0 : customerCounts[key] || 0;
  const teamTabCount = (key) =>
    key === "all" ? teamCounts.all || 0 : teamCounts[key] || 0;
  const groupTabCount = (key) =>
    key === "all" ? groupCounts.all || 0 : groupCounts[key] || 0;
  const agentTabCount = (key) => agentCounts[key] ?? 0;

  let tabs = [];
  let getCount = () => 0;
  let searchPlaceholder = "";
  let filters = [];

  if (pageKey === "customers") {
    tabs = CUSTOMER_TABS;
    getCount = customerTabCount;
    searchPlaceholder = "Search customers...";
    filters = [];
  } else if (pageKey === "teams") {
    tabs = TEAM_TABS;
    getCount = teamTabCount;
    searchPlaceholder = "Search teams...";
    filters = [];
  } else if (pageKey === "agents") {
    tabs = AGENT_TABS;
    getCount = agentTabCount;
    searchPlaceholder = "Search agents...";
    filters = [];
  } else if (pageKey === "groups") {
    tabs = GROUP_TABS;
    getCount = groupTabCount;
    searchPlaceholder = "Search groups...";
    filters = [];
  }

  return (
    <div className="people-dashboard-header-extension">
      <div className="people-header-tabs-shell">
        <div className="people-header-tabs-row">
          {tabs.map((t) => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                className={`people-header-tab-item${active ? " people-header-tab-item-active" : ""}`}
                onClick={() => setTab(t.key)}
              >
                {t.label} ({getCount(t.key)})
              </button>
            );
          })}
        </div>
      </div>

      {pageKey !== "customers" && pageKey !== "teams" && pageKey !== "agents" && (
        <PeopleToolbar
          embedded
          searchQuery={q}
          setSearchQuery={(val) => patch({ q: val || undefined })}
          searchPlaceholder={searchPlaceholder}
          filters={filters}
        />
      )}
    </div>
  );
}
